import { T, N } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, FileText } from "lucide-react";
import { Header } from "@/components/Header";
import { SiteFooter } from "@/components/SiteFooter";
import { listMyCampaigns, invoicePdfUrl } from "@/lib/api";
import { BillingCard } from "@/components/campaign/BillingFields";
import { ClientChat } from "@/components/chat/ClientChat";
import { ScanCreditsCard } from "@/components/account/ScanCreditsCard";

const LABEL = {
  pending_payment: [N("Non pagata"), "bg-[#f4f4f5] text-[#71717a]"], awaiting_start: [N("In preparazione"), "bg-[#dbeafe] text-[#1e40af]"],
  running: [N("In corso"), "bg-[#dcfce7] text-[#166534]"], paused: [N("In pausa"), "bg-[#fef3c7] text-[#92400e]"],
  completed: [N("Completata"), "bg-[#09090b] text-white"], refunded: [N("Rimborsata"), "bg-[#fee2e2] text-[#991b1b]"],
};

export default function MyCampaigns() {
  const [items, setItems] = useState(null);
  useEffect(() => { listMyCampaigns().then((d) => setItems(d.campaigns || [])).catch(() => setItems([])); }, []);

  const managed = (items || []).filter((c) => c.kind === "managed" && c.status !== "pending_payment");
  const active = managed.filter((c) => c.status !== "completed" && c.status !== "refunded");
  const history = managed.filter((c) => c.status === "completed" || c.status === "refunded");

  const Row = ({ c }) => {
    const [label, cls] = LABEL[c.status] || [c.status, ""];
    return (
      <Link to={`/campaign/${c.id}`} data-testid={`my-campaign-${c.id}`}
        className="group flex items-center gap-6 px-6 py-5 border-t border-[#e4e4e7] first:border-t-0 hover:bg-[#fafafa] transition-colors">
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg truncate">{c.params?.skill} <span className="text-[#a1a1aa]">· {c.params?.area}</span></div>
          <div className="text-xs font-mono text-[#71717a] truncate">{c.landing_url} {T("· creata il")} {new Date(c.created_at).toLocaleDateString("it-IT")}</div>
        </div>
        <span className={`text-[10px] font-mono uppercase px-2 py-1 ${cls}`}>{T(label)}</span>
        {c.invoice_number && (
          <span role="link" data-testid={`my-invoice-${c.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = invoicePdfUrl(c.id); }}
            className="cursor-pointer inline-flex items-center gap-1 text-xs font-mono border border-[#cfd6d9] px-2 py-1 hover:border-[#E0262C] hover:text-[#E0262C]">
            <FileText className="w-3 h-3" /> {c.invoice_number}
          </span>
        )}
        <ArrowRight className="w-4 h-4 text-[#a1a1aa] group-hover:text-[#09090b] group-hover:translate-x-0.5 transition-transform" />
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#E6EAEC] flex flex-col">
      <Header />
      <section data-testid="my-campaigns" className="max-w-5xl mx-auto px-6 lg:px-10 py-14 space-y-12">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-[#E0262C] mb-3">{T("Area riservata")}</div>
          <h1 className="font-display text-4xl sm:text-5xl">{T("Le mie campagne")}</h1>
        </div>
        {!items ? <Loader2 className="w-6 h-6 animate-spin text-[#E0262C]" /> : (
          <>
            <div>
              <h2 className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mb-3">{T("Attive")}</h2>
              <div data-testid="my-campaigns-active" className="border border-[#e4e4e7] bg-white">
                {active.map((c) => <Row key={c.id} c={c} />)}
                {!active.length && (
                  <div className="px-6 py-10 text-center text-sm text-[#71717a]">
                    Nessuna campagna attiva. <Link to="/" className="aurora-text font-medium">{T("Crea la tua campagna →")}</Link>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h2 className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mb-3">{T("Storico campagne")}</h2>
              <div data-testid="my-campaigns-history" className="border border-[#e4e4e7] bg-white">
                {history.map((c) => <Row key={c.id} c={c} />)}
                {!history.length && <div className="px-6 py-10 text-center text-sm text-[#a1a1aa] font-mono">{T("Nessuna campagna conclusa")}</div>}
              </div>
            </div>
            <ScanCreditsCard />
            <ClientChat />
            <BillingCard />
          </>
        )}
      </section>
      <div className="mt-auto"><SiteFooter /></div>
    </div>
  );
}
